import React, { useMemo, useCallback } from 'react';
import { Box, Button, Chip, LinearProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { ReportSectionTitle } from './ReportSectionTitle';
import { ReportMultiAutocomplete } from './ReportMultiAutocomplete';

const useStyles = makeStyles((theme) => ({
    chipBox: {
        display: 'flex',
        flexWrap: 'wrap',
        listStyle: 'none',
        padding: theme.spacing(0.5),
        margin: `8px 0`,
    },
    chip: {
        margin: theme.spacing(0.5),
    },
}));

/**
 * Componente de seleção de placas veiculares.
 * Permite ao usuário selecionar múltiplas placas usando um autocomplete
 * e visualizá-las como chips removíveis.
 *
 * @component
 * @param {Object} props
 * @param {string} props.fieldName - Nome do campo Formik a ser preenchido.
 * @param {string[]} [props.plates=[]] - Lista de todas as placas disponíveis para seleção.
 * @param {boolean} [props.disabled=false] - Se os controles devem estar desabilitados.
 * @param {boolean} [props.loading=false] - Se o botão de limpar deve estar desabilitado por carregamento.
 */
export const ReportPlateSelection = ({ plates = [], fieldName = 'placas', disabled = false, loading = false }) => {
    const classes = useStyles();
    const { values, setFieldValue } = useFormikContext();

    const chipData = useMemo(() => {
        const current = values[fieldName] || [];
        return current.map((plate) => ({ key: plate, label: plate }));
    }, [values, fieldName]);

    /**
     * Mapeia a lista de placas para objetos com chave e rótulo.
     *
     * @type {{ key: string, label: string }[]}
     */
    const platesObject = useMemo(() => plates.map((plate) => ({ key: plate, label: plate })), [plates]);

    /**
     * Remove uma placa da lista de chips.
     *
     * @param {{ key: string, label: string }} chipToDelete - Chip a ser removido.
     * @returns {() => void} Função que remove o chip ao ser chamada.
     */
    const handleDelete = useCallback(
        (chipToDelete) => () => {
            const updated = chipData.filter((chip) => chip.key !== chipToDelete.key).map((chip) => chip.key);
            setFieldValue(fieldName, updated);
        },
        [chipData, setFieldValue, fieldName],
    );

    /**
     * Limpa todas as placas selecionadas.
     */
    const handleClear = useCallback(() => {
        setFieldValue(fieldName, []);
    }, [setFieldValue, fieldName]);

    /**
     * Adiciona uma nova placa à lista de seleção.
     * Garante que placas duplicadas não sejam adicionadas.
     *
     * @param {React.ChangeEvent<{ value: string[] }>} event - Evento de seleção vindo do autocomplete.
     */
    const handleSelectPlate = useCallback(
        (event) => {
            const novasPlacas = event.target.value || [];
            const unicas = Array.from(new Set(novasPlacas));
            setFieldValue(fieldName, unicas);
        },
        [setFieldValue, fieldName],
    );

    if (loading) return <LinearProgress />;

    return (
        <>
            <ReportSectionTitle gutterBottom>Placas Veiculares:</ReportSectionTitle>
            <ReportMultiAutocomplete
                options={platesObject}
                onSelect={handleSelectPlate}
                label="Todas as placas"
                name="placas"
                valueProperty="key"
                labelProperty="label"
                getOptionLabel={(option) => option?.label ?? ''}
                renderTags={() => null} // <- Isto esconde os chips
            />
            <Box className={classes.chipBox}>
                {chipData.map((data) => (
                    <Chip key={data.key} label={data.label} onDelete={handleDelete(data)} className={classes.chip} />
                ))}
            </Box>
            <Box width="100%" display="flex" justifyContent="space-between">
                <Button
                    variant="text"
                    size="small"
                    color="default"
                    disabled={loading || disabled}
                    onClick={handleClear}>
                    Limpar seleção
                </Button>
            </Box>
        </>
    );
};
